import React, { useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

type Props = {}

type NavItem = {
  to: string
  label: string
  icon: React.ReactNode
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const NAV: NavItem[] = [
  {
    to: '/analytics',
    label: 'Analytics',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <path d="M4 19V5" /><path d="M4 19h16" />
        <path d="M8 16v-4" /><path d="M13 16V8" /><path d="M18 16v-6" />
      </svg>
    ),
  },
  {
    to: '/categories',
    label: 'Categories',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <rect x="4" y="4" width="7" height="7" rx="1.5" />
        <rect x="13" y="4" width="7" height="7" rx="1.5" />
        <rect x="4" y="13" width="7" height="7" rx="1.5" />
        <rect x="13" y="13" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    to: '/ingredients',
    label: 'Ingredients',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <path d="M5 11a7 7 0 0 1 14 0H5Z" />
        <path d="M3 11h18" />
        <path d="M6 15h12" /><path d="M8 19h8" />
      </svg>
    ),
  },
  {
    to: '/items',
    label: 'Items',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <path d="M4 9a8 8 0 0 1 16 0H4Z" />
        <path d="M3.5 13.5h17" />
        <path d="M5 17h14a0 0 0 0 1 0 0 2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z" />
      </svg>
    ),
  },
  {
    to: '/orders',
    label: 'Orders',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <path d="M7 3h10l1.5 4H5.5L7 3Z" />
        <path d="M5.5 7h13l-1 12.5a1.5 1.5 0 0 1-1.5 1.4H8a1.5 1.5 0 0 1-1.5-1.4L5.5 7Z" />
        <path d="M9.5 11v5" /><path d="M14.5 11v5" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" />
      </svg>
    ),
  },
  {
    to: '/notifications',
    label: 'Notifications',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
        <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
        <path d="M10 20a2 2 0 0 0 4 0" />
      </svg>
    ),
  },
]

const Navbar = (props: Props) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/analytics');
  }, []);

  return (
    <aside className="h-screen fixed z-10 top-0 left-0 w-72 bg-ink text-white flex flex-col">
      {/* Brand */}
      <div className="px-7 pt-8 pb-6">
        <img
          src="/ZBH_logo_white.png"
          alt="zBurgerHouse"
          className="h-9 w-auto opacity-95"
        />
        <p className="zbh-eyebrow mt-3 !text-white/40">Kitchen Console</p>
      </div>

      <div className="zbh-tear mx-7 !border-ink-line" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-4 py-5">
        <ul className="space-y-1">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  [
                    'group relative flex items-center gap-3 rounded-xl pl-5 pr-3 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-ink-soft text-white font-semibold'
                      : 'text-white/55 hover:text-white hover:bg-ink-soft/60 font-medium',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-full bg-accent transition-all',
                        isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0',
                      ].join(' ')}
                    />
                    <span className={isActive ? 'text-accent' : 'text-white/45 group-hover:text-white/80'}>
                      {item.icon}
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="px-7 py-6 border-t border-ink-line">
        <p className="text-xs text-white/40">zBurgerHouse · Zahle</p>
      </div>
    </aside>
  )
}

export default Navbar
